import { IconX } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { useDialogTrap } from '../lib/dialog-trap';
import { useClosing, useScrollLock } from '../lib/overlay';
import { buttonClass } from './ui/Button';

// Drawer / Sheet (spec §3.7, shadcn Sheet), drawn as the canvas panel's head
// row (title left, × right, a rule under it) over a scrolling body. Used by the pending table's edit
// action to show a drafted-action diff (`side="right"`, the default) and by the
// mobile picker sheet (`side="bottom"`, #5205). Open/close state lives in the
// parent; unmount is delayed by `useClosing` for the 200ms slide+fade exit.
// Focus containment from `useDialogTrap` (side-independent — the trap keys off
// the panel element, not which edge it slides from); overlay click closes.
// `initialFocus` (#6563) is passed through to that one trap, never a second one.

export type ActionDrawerProps = {
  readonly open: boolean;
  readonly title?: string;
  readonly diff?: string;
  /** A class on the panel, for a surface that sizes it (the machine details panel, #6563). */
  readonly class?: string;
  /** Which edge the panel slides from. Defaults to `'right'`. */
  readonly side?: 'right' | 'bottom';
  /**
   * Where focus lands on open (#6563): `'title'` is the heading (focusable by script,
   * never a Tab stop), a function names the element. Omitted, the trap's default: the
   * first reachable control, the close button.
   */
  readonly initialFocus?: 'title' | (() => HTMLElement | undefined);
  /** A control beside the title (the machine panel's rename pencil, #6566). */
  readonly titleAction?: JSX.Element;
  /** Extra content rendered below the diff (e.g. the PR's read-only review runs, #64). */
  readonly children?: JSX.Element;
  readonly onClose: () => void;
};

export function ActionDrawer(props: ActionDrawerProps): JSX.Element {
  let drawer: HTMLDivElement | undefined;
  let heading: HTMLHeadingElement | undefined;
  const titleFocus = (): boolean => props.initialFocus === 'title';
  useDialogTrap(
    () => props.open,
    () => drawer,
    () => props.onClose(),
    () => {
      const target = props.initialFocus;
      if (target === undefined) return undefined;
      return target === 'title' ? heading : target();
    },
  );
  useScrollLock(() => props.open);
  const gate = useClosing(() => props.open, 220);
  const side = (): 'right' | 'bottom' => props.side ?? 'right';

  return (
    <Show when={gate.visible()}>
      <div class="drawer" classList={{ 'drawer--closing': gate.closing() }}>
        <button
          type="button"
          class="drawer__overlay"
          tabindex="-1"
          aria-label={t('modal.dismiss')}
          onClick={props.onClose}
        />
        <div
          class={`drawer__panel drawer__panel--${side()}${props.class ? ` ${props.class}` : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label={props.title ?? t('drawer.diff_title')}
          tabindex="-1"
          ref={drawer}
        >
          <div class="drawer__header">
            <h2 class="drawer__title" ref={heading} tabindex={titleFocus() ? -1 : undefined}>
              {props.title ?? t('drawer.diff_title')}
            </h2>
            {props.titleAction}
            <button
              type="button"
              class={`${buttonClass({ variant: 'ghost', size: 'icon-sm' })} drawer__close`}
              aria-label={t('drawer.close')}
              onClick={props.onClose}
            >
              <IconX size="1rem" />
            </button>
          </div>
          <div class="drawer__body">
            <Show when={props.diff}>
              <pre class="code-block drawer__diff">{props.diff}</pre>
            </Show>
            {props.children}
          </div>
        </div>
      </div>
    </Show>
  );
}
